"use client";

// Motion prop/variant factories — reduced-motion-aware. Each returns props you
// spread onto a `motion.*` element (or `<Spring>`). When the user prefers
// reduced motion, every one degrades to the static end-state with an instant
// transition: no fade-in delay, no transform, no lag. Pass the result of
// `useReducedMotion()` (or use the `useMotion*` hook wrappers below, which read
// it for you) so the degradation is decided at the call site.

import { useMemo } from "react";
import { useReducedMotion } from "framer-motion";
import { INSTANT, spring, springTransition, type SpringName } from "./springs";
import type { SpringOptions } from "framer-motion";

type Preset = SpringName | SpringOptions;

/* ------------------------------------------------------------------ *
 * Hover / press affordances — subtle, soft, no bounce.               *
 * ------------------------------------------------------------------ */

/**
 * springHover — a gentle lift + scale on hover and a soft press on tap.
 * Defaults are deliberately tiny (1.5% scale, 1px lift) so it reads as
 * "responsive", never as a toy. Returns `whileHover` / `whileTap` / `transition`.
 */
export function springHover(opts?: {
  lift?: number; // px upward on hover (negative y)
  scale?: number; // hover scale
  press?: number; // tap scale
  reduce?: boolean | null;
  preset?: Preset;
}) {
  const { lift = 1, scale = 1.015, press = 0.985, reduce, preset = "snappy" } = opts ?? {};
  if (reduce) return { whileHover: undefined, whileTap: undefined, transition: INSTANT } as const;
  return {
    whileHover: { y: -lift, scale },
    whileTap: { scale: press, y: 0 },
    transition: springTransition(preset),
  } as const;
}

/* ------------------------------------------------------------------ *
 * Entrances — for panels and list rows. Drift up + fade.             *
 * ------------------------------------------------------------------ */

/**
 * springEntrance — initial/animate (and optional exit) for something arriving:
 * a small upward drift + fade that settles with weight. Reduced motion → just
 * the visible end-state, instant.
 */
export function springEntrance(opts?: {
  y?: number; // px the element rises from
  reduce?: boolean | null;
  preset?: Preset;
  exit?: boolean; // include a matching exit (for AnimatePresence)
}) {
  const { y = 8, reduce, preset = "settle", exit = false } = opts ?? {};
  if (reduce) {
    return {
      initial: false as const,
      animate: { opacity: 1, y: 0 },
      ...(exit ? { exit: { opacity: 0, transition: INSTANT } } : {}),
      transition: INSTANT,
    };
  }
  return {
    initial: { opacity: 0, y },
    animate: { opacity: 1, y: 0 },
    ...(exit ? { exit: { opacity: 0, y: y * 0.6, transition: { duration: 0.16 } } } : {}),
    transition: springTransition(preset),
  };
}

/**
 * springStagger — container variants that release children one after another.
 * Pair with `staggerChild` on each child. Reduced motion → no delay.
 */
export function springStagger(opts?: { each?: number; reduce?: boolean | null }) {
  const { each = 0.04, reduce } = opts ?? {};
  return {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : each } },
  };
}

export function staggerChild(opts?: { y?: number; reduce?: boolean | null; preset?: Preset }) {
  const { y = 8, reduce, preset = "settle" } = opts ?? {};
  if (reduce) {
    return { hidden: { opacity: 1, y: 0 }, show: { opacity: 1, y: 0, transition: INSTANT } };
  }
  return {
    hidden: { opacity: 0, y },
    show: { opacity: 1, y: 0, transition: { type: "spring" as const, ...spring(preset) } },
  };
}

/* ------------------------------------------------------------------ *
 * Hook wrappers — read reduced-motion for you, memoized.             *
 * ------------------------------------------------------------------ */

export function useSpringHover(opts?: Parameters<typeof springHover>[0]) {
  const reduce = useReducedMotion();
  return useMemo(() => springHover({ ...opts, reduce }), [opts, reduce]);
}

/* ------------------------------------------------------------------ *
 * Named hover feels for the dashboard surfaces. Thin wrappers over    *
 * springHover so every reactive control on the OS speaks one of a few *
 * shared vocabularies instead of hand-tuning numbers at each site.    *
 *   • lift  — folder/page tiles, list rows, right-rail items: a soft  *
 *             1px rise + 1.5% grow, settle on press.                  *
 *   • nudge — text-only affordances (sibling-nav words, dock links):  *
 *             no vertical lift, a barely-there 2% grow that reads as   *
 *             "alive" under the cursor without disturbing baseline.    *
 *   • press — controls that shouldn't move on hover but should        *
 *             acknowledge a tap (chips, icon buttons): hover is a      *
 *             whisper of grow, the press is the real feedback.        *
 * All inherit springHover's reduced-motion degradation (→ static).    *
 * ------------------------------------------------------------------ */
const HOVER_FEELS = {
  lift: { lift: 1, scale: 1.015, press: 0.985, preset: "snappy" as const },
  nudge: { lift: 0, scale: 1.02, press: 0.97, preset: "snappy" as const },
  press: { lift: 0, scale: 1.012, press: 0.94, preset: "snappy" as const },
} as const;

export type HoverFeel = keyof typeof HOVER_FEELS;

/**
 * useHoverFeel — the dashboard-wide hover/press affordance. Pick a named feel
 * ("lift" | "nudge" | "press") and spread the result onto a `motion.*` element.
 * Reduced-motion-aware (degrades to no transform, instant). Touch devices get
 * the same `whileTap` press, so a finger always gets acknowledged.
 */
export function useHoverFeel(feel: HoverFeel = "lift") {
  const reduce = useReducedMotion();
  return useMemo(() => springHover({ ...HOVER_FEELS[feel], reduce }), [feel, reduce]);
}

export function useSpringEntrance(opts?: Parameters<typeof springEntrance>[0]) {
  const reduce = useReducedMotion();
  return useMemo(() => springEntrance({ ...opts, reduce }), [opts, reduce]);
}
